import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import RichText from "@/components/RichText";
import ShareBar from "@/components/ShareBar";
import PlaceCard from "@/components/PlaceCard";
import { WaveDivider } from "@/components/decor";

export default function AdventurePost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [shares, setShares] = useState(0);
  const [more, setMore] = useState([]);
  const [notFound, setNotFound] = useState(false);
  useSeo(post?.seo_title || post?.title, post?.seo_description || post?.excerpt);

  useEffect(() => {
    setNotFound(false); setPost(null);
    api.get(`/blog/${slug}`).then(({ data }) => { setPost(data); setShares(data.share_count || 0); }).catch(() => setNotFound(true));
    api.get("/blog").then(({ data }) => setMore(data.posts.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <h1 className="font-display font-extrabold text-3xl text-[#19324A]">That adventure wandered off.</h1>
      <Link to="/adventures" className="btn-primary mt-6 inline-flex">Back to The Adventures</Link>
    </div>
  );
  if (!post) return <div className="max-w-3xl mx-auto px-4 py-24"><div className="h-96 rounded-3xl bg-white/60 animate-pulse" /></div>;

  return (
    <article data-testid="adventure-post">
      <section className="relative bg-[#A78BFA]/20 pt-10 pb-16 overflow-hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/adventures" className="inline-flex items-center gap-1 text-sm font-semibold text-[#A78BFA] hover:text-[#19324A]" data-testid="post-back"><ArrowLeft className="w-4 h-4" /> All adventures</Link>
          <span className="block mt-4 text-xs font-bold text-[#A78BFA] uppercase tracking-wide">{post.category}</span>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl text-[#19324A] mt-2 leading-tight" data-testid="post-title">{post.title}</h1>
          {post.published_at && <p className="mt-3 text-sm text-[#19324A]/55 inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {new Date(post.published_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>}
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-6 pb-16">
        {post.cover_image && (
          <figure className="mb-10">
            <img src={post.cover_image} alt={post.cover_caption || post.title} className="w-full aspect-[16/9] object-cover rounded-3xl soft-shadow" />
            {post.cover_caption && <figcaption className="text-xs text-[#19324A]/55 mt-2 text-center" data-testid="post-cover-caption">{post.cover_caption}</figcaption>}
          </figure>
        )}
        {post.excerpt && <p className="font-hand text-2xl text-[#FF6B9D] mb-6">{post.excerpt}</p>}
        <PlaceCard place={post.place} />
        <RichText text={post.body} />
        <div className="mt-10 rounded-3xl bg-white soft-shadow p-5" data-testid="post-share-box">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <p className="font-display font-bold text-[#19324A]">Loved this one? Share it with the Pack 🐾</p>
            {shares > 0 && <span className="text-xs font-semibold text-[#19324A]/55 rounded-full bg-[#FFF9EE] px-3 py-1" data-testid="post-share-count">Shared {shares} time{shares === 1 ? "" : "s"} by the Pack</span>}
          </div>
          <ShareBar title={post.title} path={`/api/share/post/${post.slug}`} testid="post-share" cardUrl={`${process.env.REACT_APP_BACKEND_URL}/api/blog/${post.slug}/card.png`}
            onShare={(channel) => api.post(`/blog/${post.slug}/share`, { channel }).then(({ data }) => setShares(data.share_count)).catch(() => {})}
            caption={`${post.title}\n\n${post.excerpt || ""}\n\nRead the full story 👉 ${window.location.origin}/blog/${post.slug}\n\n#TheHappyBulldogCo #ChewieAndSolo #BritishBulldog #BulldogLife #BulldogsOfInstagram #${(post.category || "").replace(/[^a-z0-9]/gi, "")}`} />
          <p className="text-xs text-[#19324A]/50 mt-3">Instagram tip: tap <strong>Share image</strong> to save the square card, then <strong>Instagram caption</strong> and paste it into your post.</p>
        </div>
        {more.length > 0 && (
          <div className="mt-16">
            <h2 className="font-display font-extrabold text-2xl text-[#19324A] mb-5">More mischief 🐾</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {more.map((p) => (
                <Link key={p.id} to={`/adventures/${p.slug}`} className="bg-white rounded-2xl overflow-hidden soft-shadow card-hover block" data-testid={`more-post-${p.slug}`}>
                  <div className="aspect-[16/10] bg-[#FFF9EE] overflow-hidden"><img src={p.cover_image} alt="" className="w-full h-full object-cover" /></div>
                  <p className="p-4 font-display font-bold text-sm text-[#19324A] leading-snug">{p.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
